/**
 * EstimateView - Joist-style estimate view page
 * Read-only document display with EDIT / EMAIL / CONVERT / MORE action bar
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { openAuthenticatedPdf } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const fmt = (v) => Number(v || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '';
  const str = typeof d === 'string' ? d : d.toISOString();
  const parts = str.split('T')[0].split('-');
  if (parts.length !== 3) return d;
  const dt = new Date(+parts[0], +parts[1] - 1, +parts[2]);
  return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const STATUS_STYLES = {
  DRAFT:    'bg-gray-100 text-gray-600',
  ISSUED:   'bg-blue-100 text-blue-700',
  APPROVED: 'bg-green-100 text-green-700',
  DECLINED: 'bg-red-100 text-red-700',
};

const EstimateView = ({ estimate: initialEstimate, onBack, onEdit, onCreateInvoice, currentUser }) => {
  const [estimate, setEstimate] = useState(initialEstimate || {});
  const [settings, setSettings] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ recipient_email: '', message: '', cc_admin: false });
  const [isSending, setIsSending] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null); // photo object for fullscreen view
  const moreRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/sales/settings`)
      .then(r => setSettings(r.data))
      .catch(e => console.error(e));
  }, []);

  useEffect(() => {
    if (!initialEstimate?.id) return;
    axios.get(`${API}/sales/estimates/${initialEstimate.id}?t=${Date.now()}`)
      .then(r => setEstimate(r.data))
      .catch(e => console.error(e));
  }, [initialEstimate?.id]);

  // Close MORE menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setShowMoreMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openSendModal = () => {
    setSendForm({
      recipient_email: estimate.client_email || '',
      message: settings?.estimate_email_message || '',
      cc_admin: false,
    });
    setShowSendModal(true);
  };

  const handleSendEstimate = async () => {
    if (!sendForm.recipient_email) { alert('Recipient email is required.'); return; }
    setIsSending(true);
    try {
      await axios.post(`${API}/sales/estimates/${estimate.id}/send`, {
        ...sendForm,
        base_url: window.location.origin
      });
      // Mark as ISSUED if DRAFT
      if (estimate.status === 'DRAFT') {
        const r = await axios.put(`${API}/sales/estimates/${estimate.id}`, { status: 'ISSUED' });
        setEstimate(r.data);
      }
      alert('Estimate sent successfully.');
      setShowSendModal(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Unable to send estimate.');
    } finally {
      setIsSending(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!estimate.id) return;
    setIsConverting(true);
    try {
      const r = await axios.post(`${API}/sales/estimates/${estimate.id}/invoice`);
      if (onCreateInvoice) onCreateInvoice(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Unable to create invoice from this estimate.');
    } finally {
      setIsConverting(false);
    }
  };

  const handleCreateJob = async () => {
    if (!estimate.id) return;
    setIsCreatingJob(true);
    try {
      const r = await axios.post(`${API}/sales/estimates/${estimate.id}/create-job`);
      setShowMoreMenu(false);
      alert(`Job created and added to queue at position #${r.data.queue_number}.`);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Unable to create job from this estimate.');
    } finally {
      setIsCreatingJob(false);
    }
  };

  const companyName = settings?.company_name || '';
  const companyCityLine = [settings?.company_city, settings?.company_province].filter(Boolean).join(', ');
  const hstNumber = settings?.hst_number || '';
  const lineItems = estimate.line_items || [];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="estimate-view-page">
      {/* ── Top Action Bar ── */}
      <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="container mx-auto px-3 sm:px-6 py-2.5 flex items-center justify-between gap-2">
          {/* Left */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 font-medium text-sm flex-shrink-0 flex items-center gap-1"
              data-testid="estimate-view-back"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Back</span>
            </button>
            <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate" data-testid="estimate-view-title">
              Estimate #{estimate.estimate_number}
            </h1>
            {estimate.status && (
              <span
                className={`hidden sm:inline-flex px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${STATUS_STYLES[estimate.status] || 'bg-gray-100 text-gray-600'}`}
                data-testid="estimate-view-status"
              >
                {estimate.status}
              </span>
            )}
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold text-xs sm:text-sm border border-gray-200 transition-colors"
              data-testid="estimate-view-edit-btn"
            >
              EDIT
            </button>

            <button
              onClick={openSendModal}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold text-xs sm:text-sm flex items-center gap-1.5 transition-colors"
              data-testid="estimate-view-email-btn"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
              <span className="hidden sm:inline">EMAIL</span>
            </button>

            {estimate.status !== 'DECLINED' && (
              <button
                onClick={handleCreateInvoice}
                disabled={isConverting}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md font-semibold text-xs sm:text-sm flex items-center gap-1.5 transition-colors"
                data-testid="estimate-view-convert-btn"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="hidden sm:inline">{isConverting ? 'Converting...' : 'CREATE INVOICE'}</span>
              </button>
            )}

            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setShowMoreMenu(p => !p)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold text-xs sm:text-sm flex items-center gap-1 border border-gray-200 transition-colors"
                data-testid="estimate-view-more-btn"
              >
                MORE
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showMoreMenu && (
                <div className="absolute right-0 mt-1.5 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30" data-testid="estimate-view-more-menu">
                  <button
                    onClick={() => { setShowMoreMenu(false); openAuthenticatedPdf(`${API}/sales/estimates/${estimate.id}/pdf?t=${Date.now()}`); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                    data-testid="estimate-view-more-pdf"
                  >
                    Download PDF
                  </button>
                  <button
                    onClick={() => { setShowMoreMenu(false); openAuthenticatedPdf(`${API}/sales/estimates/${estimate.id}/work-order-pdf?t=${Date.now()}`); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="estimate-view-more-workorder"
                  >
                    Work Order PDF
                  </button>
                  {estimate.public_token && (
                    <button
                      onClick={() => { setShowMoreMenu(false); window.open(`${window.location.origin}/estimate/${estimate.public_token}?v=${Date.now()}`, '_blank'); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="estimate-view-more-public-link"
                    >
                      Public Link
                    </button>
                  )}
                  <div className="border-t border-gray-100" />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCreateJob(); }}
                    disabled={isCreatingJob}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    data-testid="estimate-view-more-create-job"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {isCreatingJob ? 'Creating...' : 'Create Job'}
                  </button>
                  <div className="border-t border-gray-100" />
                  <button
                    onClick={() => { setShowMoreMenu(false); onEdit(); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                    data-testid="estimate-view-more-edit"
                  >
                    Edit Estimate
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Estimate Document Body ── */}
      <div className="container mx-auto px-3 sm:px-6 py-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-10" data-testid="estimate-view-document">

          {/* Document Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between gap-6 mb-8">
            {/* Left: Logo + Company */}
            <div className="flex flex-col gap-3">
              <img
                src={`${API}/sales/logo`}
                alt={companyName}
                className="h-16 w-auto object-contain object-left"
                onError={(e) => { e.target.style.display = 'none'; }}
                data-testid="estimate-view-company-logo"
              />
              <div className="text-sm text-gray-700 leading-relaxed" data-testid="estimate-view-company-info">
                <div className="font-semibold text-gray-900">{companyName}</div>
                <div>{settings?.company_address}</div>
                <div>{companyCityLine}{settings?.company_postal_code ? ` ${settings.company_postal_code}` : ''}</div>
                <div>Phone: {settings?.company_phone}</div>
                <div>Email: {settings?.company_email}</div>
                <div>Web: {settings?.company_website}</div>
              </div>
            </div>
            {/* Right: Bill To + Meta */}
            <div className="sm:text-right flex flex-col gap-4">
              <div data-testid="estimate-view-bill-to">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Prepared For</div>
                <div className="text-sm text-gray-800 leading-relaxed">
                  <div className="font-semibold text-gray-900">{estimate.client_name}</div>
                  <div>{estimate.client_address}</div>
                  <div>{[estimate.client_city, estimate.client_province].filter(Boolean).join(', ')}{estimate.client_postal_code ? ` ${estimate.client_postal_code}` : ''}</div>
                  {estimate.client_phone && <div>{estimate.client_phone}</div>}
                </div>
              </div>
              <div className="text-sm text-gray-700 space-y-1" data-testid="estimate-view-meta">
                <div className="flex sm:justify-end gap-4">
                  <span className="text-gray-500">Estimate #</span>
                  <span className="font-medium text-gray-900">{estimate.estimate_number}</span>
                </div>
                <div className="flex sm:justify-end gap-4">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-gray-900">{fmtDate(estimate.date)}</span>
                </div>
                {estimate.expiration_date && (
                  <div className="flex sm:justify-end gap-4">
                    <span className="text-gray-500">Expires</span>
                    <span className="font-medium text-gray-900">{fmtDate(estimate.expiration_date)}</span>
                  </div>
                )}
                {estimate.po_number && (
                  <div className="flex sm:justify-end gap-4">
                    <span className="text-gray-500">PO #</span>
                    <span className="font-medium text-gray-900">{estimate.po_number}</span>
                  </div>
                )}
                {hstNumber && (
                  <div className="flex sm:justify-end gap-4">
                    <span className="text-gray-500">Business / Tax #</span>
                    <span className="font-medium text-gray-900">{hstNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="mb-6" data-testid="estimate-view-line-items">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-900">
                  <th className="text-left pb-2 font-semibold text-gray-900">Description</th>
                  <th className="text-right pb-2 font-semibold text-gray-900 w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => {
                  const lines = (item.description || '').split('\n').filter(Boolean);
                  const title = lines[0] || '';
                  const body = lines.slice(1);
                  const itemTotal = (Number(item.quantity || 0) * Number(item.unit_cost || 0)) * (1 + (Number(item.markup_percentage || 0) / 100));
                  return (
                    <tr key={item.id || idx} className="border-b border-gray-100" data-testid={`estimate-view-item-${idx}`}>
                      <td className="py-3 pr-4">
                        {title && <div className="font-semibold text-gray-900">{title}</div>}
                        {body.map((l, i) => <div key={i} className="text-gray-500 text-xs mt-0.5">{l}</div>)}
                      </td>
                      <td className="py-3 text-right font-medium text-gray-900">${fmt(itemTotal)}</td>
                    </tr>
                  );
                })}
                {lineItems.length === 0 && (
                  <tr><td colSpan="2" className="py-4 text-center text-gray-400 text-sm">No line items</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-6" data-testid="estimate-view-totals">
            <div className="w-full sm:w-72 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal</span>
                <span className="font-medium">${fmt(estimate.subtotal)}</span>
              </div>
              {Number(estimate.markup_amount) > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Markup</span>
                  <span className="font-medium">${fmt(estimate.markup_amount)}</span>
                </div>
              )}
              {Number(estimate.discount_amount) > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Discount</span>
                  <span className="font-medium">-${fmt(estimate.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span>Tax ({estimate.tax_rate || 0}%)</span>
                <span className="font-medium">${fmt(estimate.tax_amount)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-900 pt-2 text-base font-bold text-gray-900">
                <span>Total (CAD)</span>
                <span data-testid="estimate-view-total">${fmt(estimate.total)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {estimate.notes && (
            <div className="border-t border-gray-100 pt-4 text-sm text-gray-700" data-testid="estimate-view-notes">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</div>
              <div className="whitespace-pre-wrap">{estimate.notes}</div>
            </div>
          )}

          {/* Contract / Terms */}
          {estimate.contract && (
            <div className="border-t border-gray-100 pt-4 text-sm text-gray-700 mt-4" data-testid="estimate-view-contract">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Terms &amp; Conditions</div>
              <div className="whitespace-pre-wrap text-xs leading-relaxed text-gray-600">{estimate.contract}</div>
            </div>
          )}

          {/* Signature status */}
          {(estimate.show_client_signature || estimate.show_my_signature) && (
            <div className="border-t border-gray-100 pt-4 mt-4 flex gap-8" data-testid="estimate-view-signatures">
              {estimate.show_my_signature && (
                <div className="text-sm text-gray-700">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Authorized By</div>
                  <div className="h-12 border-b border-gray-300 w-40" />
                  <div className="text-xs text-gray-500 mt-1">{companyName}</div>
                </div>
              )}
              {estimate.show_client_signature && (
                <div className="text-sm text-gray-700">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Client Signature</div>
                  {estimate.client_signature ? (
                    <div>
                      <img src={estimate.client_signature} alt="Client signature" className="h-12 border-b border-gray-300 w-40 object-contain object-left" />
                      <div className="text-xs text-gray-500 mt-1">{estimate.client_signature_name}</div>
                    </div>
                  ) : (
                    <div className="h-12 border-b border-gray-300 w-40 flex items-end pb-1">
                      <span className="text-xs text-gray-400">Not yet signed</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Photos Gallery ── */}
      {(estimate.photos || []).length > 0 && (
        <div className="container mx-auto px-3 sm:px-6 pb-6 max-w-4xl">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="estimate-view-photos">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4" data-testid="estimate-view-photos-heading">
              Photos ({estimate.photos.length})
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {estimate.photos.map((photo, idx) => (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => setLightboxPhoto(photo)}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100 hover:ring-2 hover:ring-emerald-500 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid={`estimate-view-photo-${idx}`}
                >
                  <img
                    src={`${API}/sales/estimates/photos/${photo.id}`}
                    alt={photo.filename || `Photo ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
          data-testid="estimate-view-lightbox"
        >
          <div className="relative max-w-5xl max-h-full w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxPhoto(null)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white text-sm font-semibold flex items-center gap-1.5"
              data-testid="estimate-view-lightbox-close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close
            </button>
            <img
              src={`${API}/sales/estimates/photos/${lightboxPhoto.id}`}
              alt={lightboxPhoto.filename}
              className="max-w-full max-h-[85vh] object-contain mx-auto rounded-lg shadow-2xl"
              data-testid="estimate-view-lightbox-img"
            />
            <div className="text-center mt-3 text-white/60 text-sm">{lightboxPhoto.filename}</div>
            {(estimate.photos || []).length > 1 && (() => {
              const photos = estimate.photos || [];
              const currentIdx = photos.findIndex(p => p.id === lightboxPhoto.id);
              const prev = photos[(currentIdx - 1 + photos.length) % photos.length];
              const next = photos[(currentIdx + 1) % photos.length];
              return (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setLightboxPhoto(prev); }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 text-white/60 hover:text-white p-2"
                    data-testid="estimate-view-lightbox-prev"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setLightboxPhoto(next); }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 text-white/60 hover:text-white p-2"
                    data-testid="estimate-view-lightbox-next"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div className="text-center mt-1 text-white/40 text-xs">{currentIdx + 1} / {photos.length}</div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Send Modal ── */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="estimate-view-send-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style={{maxHeight: '90vh'}}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 flex-shrink-0">
              <h3 className="text-base font-bold text-gray-900">Send Estimate</h3>
              <button onClick={() => setShowSendModal(false)} className="text-gray-400 hover:text-gray-600 p-1" data-testid="estimate-view-send-close">✕</button>
            </div>
            <div className="p-6 flex flex-col flex-1 overflow-y-auto gap-4" style={{minHeight: 0}}>
              <div className="flex-shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={sendForm.recipient_email}
                  onChange={(e) => setSendForm(p => ({ ...p, recipient_email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500"
                  data-testid="estimate-view-send-email"
                />
              </div>
              <div className="flex flex-col flex-1" style={{minHeight: '300px'}}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) => setSendForm(p => ({ ...p, message: e.target.value }))}
                  className="w-full flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 resize-none"
                  style={{minHeight: '300px'}}
                  data-testid="estimate-view-send-message"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={sendForm.cc_admin}
                  onChange={(e) => setSendForm(p => ({ ...p, cc_admin: e.target.checked }))}
                  className="rounded"
                  data-testid="estimate-view-send-cc-admin"
                />
                CC myself
              </label>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4 flex-shrink-0">
              <button
                onClick={() => setShowSendModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
                data-testid="estimate-view-send-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEstimate}
                disabled={isSending}
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-400"
                data-testid="estimate-view-send-confirm"
              >
                {isSending ? 'Sending...' : 'Send Estimate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateView;
